import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Share, Plus, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "vinonotas_install_dismissed";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}
function isAndroid() {
  return /android/i.test(window.navigator.userAgent);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;
    // Mostrar la invitación siempre que no esté instalada ni descartada
    setVisible(true);

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); }
      setDeferred(null);
      return;
    }
    // Sin prompt nativo disponible (iOS Safari o navegador sin soporte): mostrar instrucciones
    setShowHelp((s) => !s);
  };

  if (!visible) return null;

  const helpText = isIOS() ? (
    <>En iPhone/iPad: pulsa <Share className="inline w-4 h-4 -mt-0.5" /> <strong>Compartir</strong> y luego
      <strong> «Añadir a pantalla de inicio»</strong> <Plus className="inline w-4 h-4 -mt-0.5" />.</>
  ) : isAndroid() ? (
    <>En Android: abre el menú <strong>⋮</strong> del navegador y pulsa <strong>«Instalar aplicación»</strong> o <strong>«Añadir a pantalla de inicio»</strong>.</>
  ) : (
    <>En tu navegador de escritorio: pulsa el icono de instalar <Download className="inline w-4 h-4 -mt-0.5" /> en la barra de direcciones, o usa el menú <strong>⋮ → «Instalar Vinonotas»</strong>.</>
  );

  return (
    <Card data-testid="install-prompt" className="p-5 mb-8 border-borgona-700/15 rounded-xl bg-borgona-50 flex flex-col sm:flex-row sm:items-center gap-4">
      <span className="w-12 h-12 rounded-xl bg-borgona-700 flex items-center justify-center shrink-0">
        <Smartphone className="w-6 h-6 text-dorado-500" strokeWidth={1.75} />
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif text-lg text-negro">Instala Vinonotas en tu móvil</h3>
        <p className="text-sm text-grises-500">Accede a tus catas con un toque, incluso sin conexión.</p>
        {showHelp && (
          <p data-testid="install-help" className="text-sm text-borgona-900 mt-2 leading-relaxed">{helpText}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button data-testid="install-app-btn" onClick={install}
          className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300">
          <Download className="w-4 h-4 mr-1.5" /> Instalar app
        </Button>
        <button data-testid="install-dismiss-btn" onClick={dismiss} className="text-grises-400 hover:text-grises-600 transition-colors" aria-label="Descartar">
          <X className="w-5 h-5" />
        </button>
      </div>
    </Card>
  );
}
